import { defineStore, storeToRefs } from 'pinia'
import { useCheckoutStore } from './CheckoutStore'
import { useCouponStore } from './CouponStore'
import { usePlanStore } from './PlanStore'
import { computed } from 'vue'
import { CouponCurrency, PlanCurrency } from '@/types'
import { useOfferStore } from './OfferStore'

export const usePriceStore = defineStore('PriceStore', () => {
  const checkoutStore = useCheckoutStore()
  const planStore = usePlanStore()
  const couponStore = useCouponStore()
  const offerStore = useOfferStore()
  const { currency } = storeToRefs(checkoutStore)
  const { selectedPlanCurrency, hasSetupFee, hasUnitAmount } = storeToRefs(planStore)
  const { appliedCouponPercent, appliedCouponCurrencies } = storeToRefs(couponStore)
  const { postPromotionalOfferPlan } = storeToRefs(offerStore)

  const selectedCouponCurrency = computed(() =>
    appliedCouponCurrencies.value?.find((couponCurrency: CouponCurrency) => couponCurrency.currency === currency.value)
  )
  const postPromotionalOfferPlanCurrency = computed(() =>
    postPromotionalOfferPlan.value?.planCurrencies.find((planCurrency: PlanCurrency) => planCurrency.currency === currency.value)
  )

  const selectedCouponDiscount = computed(() => selectedCouponCurrency.value?.amount)
  const selectedPlanUnitAmount = computed(() => selectedPlanCurrency.value?.unitAmount)
  const selectedPlanSetupFee = computed(() => selectedPlanCurrency.value?.setupFee)
  const selectedPlanPricePerInterval = computed(() => selectedPlanCurrency.value?.pricePerIntervalLength)
  const selectedPlanPrice = computed(() => {
    if (hasUnitAmount.value) {
      return selectedPlanUnitAmount.value
    }

    if (hasSetupFee.value) {
      return selectedPlanSetupFee.value
    }
    return undefined
  })

  const selectedPlanPercentageDiscountPrice = computed(() => {
    const percentage = appliedCouponPercent.value
    const price = selectedPlanPrice.value
    return percentage ? price - price * (percentage / 100) : null
  })

  const selectedPlanFixedDiscountPrice = computed(() => {
    const price = selectedPlanPrice.value
    return selectedCouponDiscount.value ? price - selectedCouponDiscount.value : null
  })

  const formatPrice = (price: number) => {
    const currency = selectedPlanCurrency.value?.currency

    // Degrade gracefully before a plan/currency is resolved: Intl.NumberFormat throws
    // ("Currency code is required with currency style") when currency is undefined, and formats a
    // bare price as "$NaN". Both surface as uncaught errors when the price computeds are evaluated
    // early (e.g. by the Pinia devtools inspector) before a plan is selected.
    if (!currency || price == null) {
      return ''
    }

    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(price)
  }

  const selectedPlanCouponAppliedPrice = computed(() => {
    const price = selectedPlanPrice.value
    const discountedPrice = selectedPlanPercentageDiscountPrice.value ?? selectedPlanFixedDiscountPrice.value

    return discountedPrice ?? price
  })

  const selectedPlanDisplayPrice = computed(() => formatPrice(selectedPlanCouponAppliedPrice.value))

  const selectedPlanDisplayPricePerInterval = computed(() => {
    return formatPrice(selectedPlanPricePerInterval.value)
  })

  const postPromoOfferPlanPrice = computed(() => {
    return postPromotionalOfferPlanCurrency.value?.basePrice
  })

  const postPromoOfferPlanDisplayPrice = computed(() => {
    return postPromoOfferPlanPrice.value ? formatPrice(postPromoOfferPlanPrice.value) : undefined
  })

  const selectedPlanDisplayUnitPrice = computed(() => {
    return formatPrice(selectedPlanPrice.value)
  })

  return {
    postPromoOfferPlanDisplayPrice,
    postPromoOfferPlanPrice,
    selectedCouponCurrency,
    selectedCouponDiscount,
    selectedPlanFixedDiscountPrice,
    selectedPlanCouponAppliedPrice,
    selectedPlanCurrency,
    selectedPlanDisplayPrice,
    selectedPlanDisplayPricePerInterval,
    selectedPlanDisplayUnitPrice,
    selectedPlanPercentageDiscountPrice,
    selectedPlanSetupFee,
    selectedPlanUnitAmount
  }
})
